import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { faCheck, faCircleXmark, faEllipsisVertical, faEye } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import type { CustomColDef } from '@shared/components/ui/AgGridTable';
import { usePermissions } from '@shared/hooks/usePermissions';
import { type ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Topup, TopupStatus } from '../models';

const formatCurrency = (value?: number, currency?: string) => {
  if (value === undefined || value === null) return '—';
  return formatCurrencyAmount(value, currency || 'EUR');
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat('es-ES', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const maskIban = (iban: string) => {
  if (!iban || iban.length < 8) return iban;
  const visible = iban.slice(0, 4) + '...' + iban.slice(-4);
  return visible;
};

export const getStatusBadge = (status: TopupStatus, t: TFunction<'topupsModule'>) => {
  const map: Record<
    TopupStatus,
    {
      variant: 'default' | 'destructive' | 'secondary' | 'outline' | 'transparent-green' | 'transparent-orange';
      label: string;
    }
  > = {
    PENDING_REVIEW: { variant: 'transparent-orange', label: t('status.pending') },
    APPROVED: { variant: 'transparent-green', label: t('status.approved') },
    REJECTED: { variant: 'destructive', label: t('status.rejected') },
  };
  const config = map[status] ?? map.PENDING_REVIEW;
  return (
    <Badge variant={config.variant} className="text-xs">
      {config.label}
    </Badge>
  );
};

export type TopupsColumnDef = ColumnDef<Topup>;

export interface UseTopupsColumnsOptions {
  onApprove?: (topup: Topup) => void;
  onReject?: (topup: Topup) => void;
  onViewDetail?: (topup: Topup) => void;
  selectedColumns?: string[];
}

const buildTopupsColumns = (
  t: TFunction<'topupsModule'>,
  options: UseTopupsColumnsOptions,
  is3pl: boolean,
  canApprove: boolean,
  canReject: boolean,
  currency: string,
): TopupsColumnDef[] => {
  const { onApprove, onReject, onViewDetail } = options;

  const columns: TopupsColumnDef[] = [
    {
      id: 'payment_method',
      accessorKey: 'payment_method',
      header: t('table.columns.payment_method'),
      cell: ({ row }) => <span className="text-sm">{row.original.payment_method}</span>,
    },
    {
      id: 'declared_amount',
      header: t('table.columns.declared_amount'),
      cell: ({ row }) => (
        <span className="text-sm font-medium">{formatCurrency(row.original.declared_amount, currency)}</span>
      ),
    },
    {
      id: 'confirmed_amount',
      header: t('table.columns.confirmed_amount'),
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.confirmed_amount !== null ? formatCurrency(row.original.confirmed_amount, currency) : '—'}
        </span>
      ),
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: t('table.columns.status'),
      cell: ({ row }) => getStatusBadge(row.original.status, t),
    },
    {
      id: 'payment_reference',
      accessorKey: 'payment_reference',
      header: t('table.columns.payment_reference'),
      cell: ({ row }) => <span className="text-sm font-mono">{row.original.payment_reference}</span>,
    },
    {
      id: 'transfer_date',
      accessorKey: 'transfer_date',
      header: t('table.columns.transfer_date'),
      cell: ({ row }) => <span className="text-sm">{formatDate(row.original.transfer_date)}</span>,
    },
    {
      id: 'payer_name',
      accessorKey: 'payer_name',
      header: t('table.columns.payer_name'),
      cell: ({ row }) => <span className="text-sm">{row.original.payer_name}</span>,
    },
    {
      id: 'payer_iban',
      accessorKey: 'payer_iban',
      header: t('table.columns.payer_iban'),
      cell: ({ row }) => <span className="text-sm font-mono">{maskIban(row.original.payer_iban)}</span>,
    },
  ];

  if (is3pl) {
    columns.push(
      {
        id: 'wallet_id',
        accessorKey: 'wallet_id',
        header: t('table.columns.client'),
        cell: ({ row }) => (
          <span className="text-sm">{row.original.wallet?.team?.name ?? `#${row.original.wallet_id}`}</span>
        ),
      },
      {
        id: 'requested_by_user_id',
        header: t('table.columns.requested_by'),
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.requested_by_user?.name ??
              (row.original.requested_by_user_id ? `#${row.original.requested_by_user_id}` : '—')}
          </span>
        ),
      },
      {
        id: 'reviewed_by_user_id',
        header: t('table.columns.reviewed_by'),
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.reviewed_by_user?.name ??
              (row.original.reviewed_by_user_id ? `#${row.original.reviewed_by_user_id}` : '—')}
          </span>
        ),
      },
    );
  }

  // Acciones
  columns.push({
    id: 'actions',
    header: '',
    enableSorting: false,
    cell: ({ row }) => {
      const topup = row.original;
      const isPending = topup.status === 'PENDING_REVIEW';

      const actionItems: ReactNode[] = [];

      actionItems.push(
        <DropdownMenuItem key="view" onClick={() => onViewDetail?.(topup)}>
          <FontAwesomeIcon icon={faEye} className="w-4 h-4 mr-2" />
          {t('actions.view')}
        </DropdownMenuItem>,
      );

      if (isPending && is3pl) {
        if (canApprove) {
          actionItems.push(
            <DropdownMenuItem key="approve" onClick={() => onApprove?.(topup)}>
              <FontAwesomeIcon icon={faCheck} className="w-4 h-4 mr-2 text-green-600" />
              {t('actions.approve')}
            </DropdownMenuItem>,
          );
        }
        if (canReject) {
          actionItems.push(
            <DropdownMenuItem key="reject" onClick={() => onReject?.(topup)} className="text-destructive">
              <FontAwesomeIcon icon={faCircleXmark} className="w-4 h-4 mr-2" />
              {t('actions.reject')}
            </DropdownMenuItem>,
          );
        }
      }

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 px-0" aria-label={t('table.columns.actions')}>
              <FontAwesomeIcon icon={faEllipsisVertical} className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[200px]">
            {actionItems.length ? actionItems : <DropdownMenuItem disabled>{t('noResults')}</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  });

  return columns;
};

const mapToAgGridColumns = (columns: TopupsColumnDef[]): CustomColDef[] => {
  return columns.map((column) => {
    const accessorKey =
      'accessorKey' in column && typeof column.accessorKey === 'string' ? column.accessorKey : undefined;

    const columnId = (column.id as string) || accessorKey || String((column as any).accessorKey ?? '');

    const isActionsColumn = columnId === 'actions';

    return {
      colId: columnId,
      field: accessorKey && !isActionsColumn ? accessorKey : undefined,
      headerName: typeof column.header === 'string' ? column.header : undefined,
      pinned: isActionsColumn ? 'right' : undefined,
      width: (column as any).size,
      minWidth: (column as any).minWidth,
      maxWidth: (column as any).maxWidth,
      sortable: column.enableSorting !== false,
      lockPosition: isActionsColumn ? 'right' : undefined,
      suppressMovable: isActionsColumn,
      suppressAutoSize: isActionsColumn,
      suppressSizeToFit: isActionsColumn,
      cellRenderer: (params: any) => {
        if (typeof column.cell !== 'function') {
          return params.value;
        }

        return column.cell({
          row: { original: params.data },
          getValue: () => params.value,
        } as any);
      },
    } as CustomColDef;
  });
};

export interface UseTopupsColumnsResult {
  tanStackColumns: TopupsColumnDef[];
  agGridColumnDefs: CustomColDef[];
  filteredColumnDefs: CustomColDef[];
}

export const useTopupsColumns = (options?: UseTopupsColumnsOptions): UseTopupsColumnsResult => {
  const { t } = useTranslation('topupsModule');
  const { canAccess } = usePermissions();
  const currency = useCurrency();
  const is3pl = canAccess(['tenant_3pl'], []); // Base 3PL check
  const canApprove = canAccess(['tenant_3pl'], ['topups.approve']);
  const canReject = canAccess(['tenant_3pl'], ['topups.reject']);

  const tanStackColumns = useMemo<TopupsColumnDef[]>(
    () => buildTopupsColumns(t, options ?? {}, is3pl, canApprove, canReject, currency),
    [t, options, is3pl, canApprove, canReject, currency],
  );

  const agGridColumnDefs = useMemo(() => mapToAgGridColumns(tanStackColumns), [tanStackColumns]);

  const filteredColumnDefs = useMemo(() => {
    const selectedIds = options?.selectedColumns;
    if (!selectedIds || selectedIds.length === 0) {
      return agGridColumnDefs;
    }

    return agGridColumnDefs.filter((column) => selectedIds.includes(String(column.colId)));
  }, [agGridColumnDefs, options?.selectedColumns]);

  return {
    tanStackColumns,
    agGridColumnDefs,
    filteredColumnDefs,
  };
};
